import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, AlertCircle } from 'lucide-react';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const VoiceInput = ({ onTranscript, disabled = false, className = '' }) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const safetyRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SpeechRecognition);
  }, []);

  const forceCleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (safetyRef.current) {
      clearTimeout(safetyRef.current);
      safetyRef.current = null;
    }
    const rec = recognitionRef.current;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      rec.onstart = null;
      try { rec.abort(); } catch {}
    }
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      forceCleanup();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    // Always create a fresh instance
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    let gotResult = false;

    recognition.onresult = (event) => {
      gotResult = true;
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript && onTranscript) {
        onTranscript(transcript.trim());
      }
      // Force cleanup right after result on iOS
      forceCleanup();
    };

    recognition.onerror = (e) => {
      // On iOS, "no-speech" means Siri might be disabled
      if (isIOS() && (e.error === 'no-speech' || e.error === 'not-allowed')) {
        setShowIOSHint(true);
        setTimeout(() => setShowIOSHint(false), 6000);
      }
      forceCleanup();
    };

    recognition.onend = () => {
      // If no result was received and we're on iOS, show hint
      if (!gotResult && isIOS()) {
        setShowIOSHint(true);
        setTimeout(() => setShowIOSHint(false), 6000);
      }
      forceCleanup();
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
      setIsListening(true);

      // Auto-abort after 10s (shorter = safer on iOS)
      timeoutRef.current = setTimeout(() => {
        forceCleanup();
      }, 10000);

      // Safety net: if onend never fires (iOS bug), force cleanup after 12s
      safetyRef.current = setTimeout(() => {
        if (recognitionRef.current) {
          forceCleanup();
        }
      }, 12000);
    } catch {
      forceCleanup();
    }
  }, [isListening, onTranscript, forceCleanup]);

  useEffect(() => {
    return () => {
      forceCleanup();
    };
  }, [forceCleanup]);

  if (!isSupported) return null;

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={toggleListening}
        disabled={disabled}
        className={`inline-flex items-center justify-center rounded-lg transition-all duration-200 ${
          isListening
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30 animate-pulse'
            : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800'
        } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
        title={isListening ? 'Arreter la dictee' : 'Dicter avec le micro'}
        data-testid="voice-input-btn"
      >
        {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
      {showIOSHint && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-2 shadow-lg z-50"
          data-testid="voice-ios-hint"
        >
          <div className="flex items-start gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Sur iPhone, activez Siri dans Reglages pour utiliser la dictee vocale.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
